<div class="dependency-tree-wrapper">

  <!-- Header -->
  <div class="flex flex-row items-center justify-between mb-16">
    <div class="flex flex-col">
      <span class="fs-18 fw-600">Dependency Tree@if (instructionType) { - {{ instructionType }}}</span>
      <span class="fs-12 text-secondary mt-4">Component dependency hierarchy for this environment</span>
    </div>
    <button class="tree-refresh-btn" (click)="loadDependencyTree()" [disabled]="isLoading" matTooltip="Refresh">
      <mat-icon svgIcon="refresh"></mat-icon>
    </button>
  </div>

  <!-- Tabs -->
  <mat-tab-group [(selectedIndex)]="activeTab" animationDuration="150ms" class="dependency-tabs">

    <!-- ── Tab 1: Tree ──────────────────────────────────────── -->
    <mat-tab label="Tree">

      <!-- Search -->
      <app-search-filter class="mt-16 mb-12 d-block" placeholder="Search by name, type, version or status..." [searchText]="searchTerm" (changed)="onSearch($event)"></app-search-filter>

      <!-- Loading skeleton -->
      @if (isLoading) {
        <div class="flex flex-col">
          @for (i of [1, 2, 3, 4, 5, 6]; track i) {
            <ngx-skeleton-loader
              [theme]="{ height: '44px', borderRadius: '6px', marginBottom: '4px' }"
            ></ngx-skeleton-loader>
          }
        </div>
      }

      <!-- Error state -->
      @if (!isLoading && hasError) {
        <div class="flex flex-col items-center justify-center tree-empty-state">
          <mat-icon svgIcon="error_outline" class="empty-state-icon text-error mb-12"></mat-icon>
          <p class="fs-14 text-secondary mb-16">Failed to load dependency tree. Please try again.</p>
          <button class="btn-primary h-36 px-16 fs-14 fw-500" (click)="loadDependencyTree()">Retry</button>
        </div>
      }

      <!-- Empty state -->
      @if (!isLoading && !hasError && treeData.length === 0) {
        <div class="flex flex-col items-center justify-center tree-empty-state">
          <mat-icon svgIcon="list" class="empty-state-icon mb-12"></mat-icon>
          <p class="fs-14 text-secondary">No dependency data available for this environment.</p>
        </div>
      }

      <!-- Tree -->
      @if (!isLoading && !hasError && treeData.length > 0) {
        <div class="tree-container" role="tree">
          <ng-container *ngTemplateOutlet="treeNodes; context: { $implicit: treeData, level: 0 }"></ng-container>
        </div>
      }

    </mat-tab>

    <!-- ── Tab 2: Details ───────────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        Details
        @if (selectedNode) {
          <span class="tab-selected-badge ml-6">{{ selectedNode.name }}</span>
        }
      </ng-template>

      @if (!selectedNode) {
        <div class="flex flex-col items-center justify-center tree-empty-state mt-16">
          <mat-icon svgIcon="description" class="empty-state-icon mb-12"></mat-icon>
          <p class="fs-14 text-secondary">Click a component name in the Tree tab to view its details.</p>
        </div>
      }

      @if (selectedNode) {
        <div class="detail-panel mt-16">

          <!-- Component header -->
          <div class="detail-header mb-20">
            <span class="fs-16 fw-700 detail-title">{{ selectedNode.name }}</span>
            @if (selectedNode.type) {
              <span class="node-badge type-badge ml-8">{{ selectedNode.type }}</span>
            }
            @if (selectedNode.version) {
              <span class="node-version fs-12 ml-8">v{{ selectedNode.version }}</span>
            }
            @if (selectedNode.status) {
              <span class="node-badge status-badge ml-8" [ngClass]="getStatusClass(selectedNode.status)">{{ selectedNode.status }}</span>
            }
          </div>

          <!-- Key fields -->
          @if (selectedNode.rawData?.description) {
            <div class="detail-field">
              <span class="detail-label">Description</span>
              <span class="detail-value">{{ selectedNode.rawData.description }}</span>
            </div>
          }
          @if (selectedNode.rawData?.capability) {
            <div class="detail-field">
              <span class="detail-label">Capability</span>
              <span class="detail-value">{{ selectedNode.rawData.capability }}</span>
            </div>
          }
          @if (selectedNode.rawData?.componentDeployedName) {
            <div class="detail-field">
              <span class="detail-label">Deployed Name</span>
              <span class="detail-value">{{ selectedNode.rawData.componentDeployedName }}</span>
            </div>
          }
          @if (selectedNode.rawData?.releaseVersion) {
            <div class="detail-field">
              <span class="detail-label">Release Version</span>
              <span class="detail-value">{{ selectedNode.rawData.releaseVersion }}</span>
            </div>
          }
          @if (selectedNode.rawData?.packageURI) {
            <div class="detail-field">
              <span class="detail-label">Package URI</span>
              <span class="detail-value detail-value--uri" [title]="selectedNode.rawData.packageURI">{{ selectedNode.rawData.packageURI }}</span>
            </div>
          }
          @if (selectedNode.rawData?.useStableArtifacts !== undefined) {
            <div class="detail-field">
              <span class="detail-label">Stable Artifacts</span>
              <span class="detail-value">{{ selectedNode.rawData.useStableArtifacts }}</span>
            </div>
          }
          @if (selectedNode.rawData?.isDevComponent !== undefined) {
            <div class="detail-field">
              <span class="detail-label">Dev Component</span>
              <span class="detail-value">{{ selectedNode.rawData.isDevComponent }}</span>
            </div>
          }

          <!-- Actions -->
          @if (selectedNode.rawData?.actions?.length) {
            <div class="detail-section">
              <div class="detail-section-title">Actions</div>
              <div class="detail-chips">
                @for (action of selectedNode.rawData.actions; track action) {
                  <span class="node-badge type-badge">{{ action }}</span>
                }
              </div>
            </div>
          }

          <!-- Params -->
          @if (selectedNode.rawData?.params && objectKeys(selectedNode.rawData.params).length) {
            <div class="detail-section">
              <div class="detail-section-title">Params</div>
              @for (key of objectKeys(selectedNode.rawData.params); track key) {
                <div class="detail-field detail-field--indent">
                  <span class="detail-label">{{ key }}</span>
                  <span class="detail-value">{{ selectedNode.rawData.params[key] }}</span>
                </div>
              }
            </div>
          }

          <!-- Dependencies -->
          @if (selectedNode.rawData?.dependencies) {
            <div class="detail-section">
              <div class="detail-section-title">Dependencies</div>
              <pre class="detail-json">{{ selectedNode.rawData.dependencies | json }}</pre>
            </div>
          }

        </div>
      }

    </mat-tab>

  </mat-tab-group>

</div>

<!-- Recursive tree node template -->
<ng-template #treeNodes let-nodes let-level="level">
  @for (node of nodes; track node.name) {

    <!-- Node row -->
    <div
      class="tree-node-row"
      role="treeitem"
      [style.padding-left.px]="level * 24 + 8"
      [class.tree-node-group]="node.isGroup"
      [attr.aria-expanded]="node.children?.length ? isExpanded(node) : null"
    >
      <!-- Toggle button or spacer -->
      @if (node.children?.length) {
        <button class="toggle-btn" (click)="toggleNode(node)" [attr.aria-label]="'Toggle ' + node.name">
          <svg class="toggle-arrow" [class.toggle-arrow--expanded]="isExpanded(node)"
               width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z"/>
          </svg>
        </button>
      } @else {
        <span class="toggle-spacer"></span>
      }

      <!-- Group node (TBC / TC / Infra) -->
      @if (node.isGroup) {
        <mat-icon class="node-icon group-icon" [svgIcon]="getGroupIcon(node.name)"></mat-icon>
        <span class="group-label fw-700 fs-14">{{ node.name }}</span>
        <span class="child-count-badge ml-8">{{ node.children?.length }}</span>
      } @else {
        <!-- Regular component node -->
        @if (node.children?.length) {
          <mat-icon svgIcon="description" class="node-icon parent-icon"></mat-icon>
        } @else {
          <mat-icon svgIcon="radio_button_unchecked" class="node-icon leaf-icon"></mat-icon>
        }
        <div class="node-info">
          <span
            class="node-name fs-14 node-name-link"
            [class.fw-600]="node.children?.length"
            [class.fw-500]="!node.children?.length"
            [class.node-name-active]="selectedNode === node"
            (click)="selectNode(node)"
          >{{ node.name }}</span>
          @if (node.type) {
            <span class="node-badge type-badge ml-8">{{ node.type }}</span>
          }
          @if (node.version) {
            <span class="node-version fs-12 ml-8">v{{ node.version }}</span>
          }
          @if (node.status) {
            <span class="node-badge status-badge ml-8" [ngClass]="getStatusClass(node.status)">{{ node.status }}</span>
          }
        </div>
      }
    </div>

    <!-- Children (rendered recursively) -->
    @if (node.children?.length && isExpanded(node)) {
      <ng-container *ngTemplateOutlet="treeNodes; context: { $implicit: node.children ?? [], level: level + 1 }"></ng-container>
    }

  }
</ng-template>
